/**
 * Global worklist ordering and rank normalization (STRT-359).
 * Used by the game plan store and worklists list UI.
 */

/** Unassigned / catch-all row: fixed at bottom; excluded from manual reorder. */
export function isPinnedWorklist(wl) {
  return !!wl?.is_default;
}

/** Normalized work list name for duplicate checks (align with saveWorkStrategy). */
export function worklistNameUniquenessKey(name) {
  return (name ?? '').toString().trim().toLowerCase();
}

/** Same numeric coercion as getSaveState work_list_type. */
export function normalizeWorkListTypeId(type) {
  return Number(type) || 5;
}

export function compareGlobalRank(a, b) {
  if (isPinnedWorklist(a) && !isPinnedWorklist(b)) return 1;
  if (!isPinnedWorklist(a) && isPinnedWorklist(b)) return -1;

  const dr = (a.rank ?? 0) - (b.rank ?? 0);
  if (dr !== 0) return dr;
  return (a.work_list_id ?? 0) - (b.work_list_id ?? 0);
}

/**
 * Contiguous global ranks: movable lists first (1..M), then pinned lists (M+1..).
 * Preserves relative order within each group using current rank.
 */
export function normalizeWorklistRanks(worklists) {
  const movable = worklists.filter(w => !isPinnedWorklist(w));
  const pinned = worklists.filter(w => isPinnedWorklist(w));
  movable.sort(compareGlobalRank);
  pinned.sort(compareGlobalRank);
  let r = 1;
  const idToRank = new Map();
  for (const w of movable) idToRank.set(w.work_list_id, r++);
  for (const w of pinned) idToRank.set(w.work_list_id, r++);
  return worklists.map(w => ({
    ...w,
    rank: idToRank.get(w.work_list_id) ?? w.rank,
  }));
}

/**
 * Puts a single worklist (newly created, duplicated, or moved by type/priority) immediately
 * after the last row of the same `(work_list_type, is_priority)` in the **current** rank order.
 * All other worklists keep their relative order; only ranks are reassigned 1..N (then pinned).
 * Does not resort the list by work_list_type (unlike a global "type group" re-sort).
 */
export function insertWorklistAfterLastOfSameType(worklists, newOrMoved) {
  const id = newOrMoved.work_list_id;
  const others = worklists.filter(w => w.work_list_id !== id);
  const pinned = others.filter(isPinnedWorklist);
  const movable = others.filter(w => !isPinnedWorklist(w));
  const sortedMovable = [...movable].sort(compareGlobalRank);
  const t = Number(newOrMoved.work_list_type) || 5;
  const p = !!newOrMoved.is_priority;
  let insertIndex = sortedMovable.length;
  for (let i = sortedMovable.length - 1; i >= 0; i--) {
    const w = sortedMovable[i];
    if (Number(w.work_list_type) === t && !!w.is_priority === p) {
      insertIndex = i + 1;
      break;
    }
  }
  const combinedMovable = [
    ...sortedMovable.slice(0, insertIndex),
    newOrMoved,
    ...sortedMovable.slice(insertIndex),
  ];
  let r = 1;
  const idToRank = new Map();
  for (const w of combinedMovable) {
    idToRank.set(w.work_list_id, r++);
  }
  for (const w of [...pinned].sort(compareGlobalRank)) {
    idToRank.set(w.work_list_id, r++);
  }
  return [...others, newOrMoved].map(w => ({
    ...w,
    rank: idToRank.get(w.work_list_id) ?? w.rank,
  }));
}
